import React, { useState, useEffect } from "react";
import {
  Container,
  Paper,
  Typography,
  Box,
  Card,
  CardContent,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Tab,
  Tabs,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Select,
  MenuItem,
  FormControl,
} from "@mui/material";
import { CloudUpload, Map as MapIcon, ArrowBack } from "@mui/icons-material";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";

function FieldDataManagement() {
  const token = localStorage.getItem("token");
  const navigate = useNavigate();
  const location = useLocation();
  const storedCropType = (localStorage.getItem("cropType") || "").toLowerCase();
  const navigationCropType = (location.state?.cropType || "").toLowerCase();
  const activeAdminCrop = (
    localStorage.getItem("activeAdminCrop") || ""
  ).toLowerCase();
  const effectiveCropType =
    storedCropType === "all"
      ? navigationCropType || activeAdminCrop || "corn"
      : storedCropType === "cotton"
        ? "cotton"
        : "corn";
  const cropLabel = effectiveCropType === "cotton" ? "Cotton" : "Corn";
  const [dataType, setDataType] = useState(0); // 0 for Plant Tissue, 1 for Soil
  const [fieldDataFile, setFieldDataFile] = useState(null);
  const [uploadingFieldData, setUploadingFieldData] = useState(false);
  const [parsedFieldData, setParsedFieldData] = useState(null);
  const [soilDataDate, setSoilDataDate] = useState(""); // Date for corn soil data
  const [plantTissueDate, setPlantTissueDate] = useState(""); // Date for plant tissue data
  const [allTeams, setAllTeams] = useState([]);
  const [notification, setNotification] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  useEffect(() => {
    fetchAllTeams();
  }, [effectiveCropType]);

  const fetchAllTeams = async () => {
    try {
      const endpoint =
        effectiveCropType === "cotton"
          ? "/api/get_cotton_users"
          : "/api/get_corn_users";

      const response = await axios.get(endpoint, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const teams = (response.data || []).map((user) => ({
        ...user,
        crop: cropLabel,
      }));
      setAllTeams(teams);
    } catch (error) {
      console.error("Error fetching teams:", error);
    }
  };

  const showNotification = (message, severity = "success") => {
    setNotification({ open: true, message, severity });
    setTimeout(
      () => setNotification({ open: false, message: "", severity: "success" }),
      5000,
    );
  };

  const handleFieldDataFileUpload = async () => {
    if (!fieldDataFile) {
      showNotification("Please select a file to upload.", "warning");
      return;
    }

    // Validate date for soil data (both crops)
    if (dataType === 1 && !soilDataDate) {
      showNotification("Please select a date for the soil data.", "warning");
      return;
    }

    // Validate date for plant tissue data
    if (dataType === 0 && !plantTissueDate) {
      showNotification(
        "Please select a date for the plant tissue data.",
        "warning",
      );
      return;
    }

    setUploadingFieldData(true);
    const formData = new FormData();
    formData.append("file", fieldDataFile);
    formData.append("dataType", dataType === 0 ? "plantTissue" : "soil");
    formData.append("cropType", effectiveCropType);

    // Add soil data date
    if (dataType === 1 && soilDataDate) {
      formData.append("soilDataDate", soilDataDate);
    }

    // Add plant tissue data date
    if (dataType === 0 && plantTissueDate) {
      formData.append("plantTissueDate", plantTissueDate);
    }

    try {
      const response = await axios.post(
        "/api/uploadPlantTissueData",
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${token}`,
          },
        },
      );

      if (response.data.success) {
        showNotification(
          `Successfully processed ${response.data.insertedCount} teams! Data has been automatically assigned to the correct teams.`,
          "success",
        );
        setParsedFieldData(response.data.parsedData);
        setFieldDataFile(null);
        setSoilDataDate(""); // Clear the date field after successful upload
        setPlantTissueDate(""); // Clear the date field after successful upload
      } else {
        showNotification(
          "Error uploading file: " + response.data.error,
          "error",
        );
      }
    } catch (error) {
      console.error("Error uploading field data file:", error);
      showNotification(
        "Error uploading file: " +
          (error.response?.data?.error || error.message),
        "error",
      );
    } finally {
      setUploadingFieldData(false);
    }
  };

  const handleFieldDataFileChange = (event) => {
    setFieldDataFile(event.target.files[0]);
    setParsedFieldData(null);
  };

  const getTodayDate = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, "0");
    const day = String(today.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      {/* Back Button */}
      <Button
        variant="outlined"
        startIcon={<ArrowBack />}
        onClick={() => navigate(-1)}
        sx={{ mb: 2 }}
      >
        Back
      </Button>

      {/* Header */}
      <Paper
        elevation={3}
        sx={{
          p: 3,
          mb: 3,
          background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
        }}
      >
        <Typography
          variant="h4"
          sx={{
            color: "white",
            fontWeight: "bold",
            display: "flex",
            alignItems: "center",
          }}
        >
          <MapIcon sx={{ mr: 2, fontSize: 40 }} />
          Field Data Management Center
        </Typography>
        <Typography
          variant="subtitle1"
          sx={{ color: "rgba(255,255,255,0.9)", mt: 1 }}
        >
          Upload and manage plant tissue and soil data for {cropLabel} teams
        </Typography>
      </Paper>

      {notification.open && (
        <Box sx={{ mb: 3 }}>
          <Paper
            elevation={3}
            sx={{
              p: 2,
              bgcolor:
                notification.severity === "success"
                  ? "#d4edda"
                  : notification.severity === "warning"
                    ? "#fff3cd"
                    : "#f8d7da",
              borderLeft: `4px solid ${notification.severity === "success" ? "#28a745" : notification.severity === "warning" ? "#ffc107" : "#dc3545"}`,
            }}
          >
            <Typography variant="body1" sx={{ color: "#000" }}>
              {notification.message}
            </Typography>
          </Paper>
        </Box>
      )}

      <Grid container spacing={3}>
        {/* Left side - Map of Teams */}
        <Grid item xs={12} md={4}>
          <Card elevation={3} sx={{ height: "100%" }}>
            <CardContent>
              <Typography
                variant="h6"
                gutterBottom
                sx={{ display: "flex", alignItems: "center", color: "#667eea" }}
              >
                <MapIcon sx={{ mr: 1 }} />
                Registered Teams ({allTeams.length})
              </Typography>
              <Divider sx={{ my: 2 }} />
              <Box sx={{ maxHeight: "600px", overflow: "auto" }}>
                {allTeams.length > 0 ? (
                  allTeams.map((team, index) => (
                    <Paper
                      key={index}
                      elevation={1}
                      sx={{
                        p: 2,
                        mb: 2,
                        borderLeft: `4px solid ${team.crop === "Corn" ? "#FFA726" : "#66BB6A"}`,
                        "&:hover": {
                          boxShadow: 3,
                          transform: "translateX(5px)",
                          transition: "all 0.3s ease",
                        },
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                        }}
                      >
                        <Box>
                          <Typography variant="subtitle1" fontWeight="bold">
                            {team.teamName}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {team.email}
                          </Typography>
                        </Box>
                        <Chip
                          label={team.crop}
                          size="small"
                          color={team.crop === "Corn" ? "warning" : "success"}
                        />
                      </Box>
                    </Paper>
                  ))
                ) : (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    align="center"
                  >
                    No teams registered yet
                  </Typography>
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Right side - File Upload */}
        <Grid item xs={12} md={8}>
          <Card elevation={3}>
            <CardContent>
              <Typography
                variant="h6"
                gutterBottom
                color="primary"
                sx={{ display: "flex", alignItems: "center" }}
              >
                <CloudUpload sx={{ mr: 1 }} />
                Upload Field Data
              </Typography>
              <Divider sx={{ my: 2 }} />

              {/* Data Type Tabs */}
              <Paper elevation={2} sx={{ mb: 3 }}>
                <Tabs
                  value={dataType}
                  onChange={(e, newValue) => {
                    setDataType(newValue);
                    setParsedFieldData(null);
                    setFieldDataFile(null);
                  }}
                  variant="fullWidth"
                  indicatorColor="primary"
                  textColor="primary"
                >
                  <Tab label="Plant Tissue Data" />
                  <Tab label="Soil Data" />
                </Tabs>
              </Paper>

              {/* File Upload Section */}
              <Box sx={{ p: 3, bgcolor: "#f5f5f5", borderRadius: 2 }}>
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                  {dataType === 0
                    ? "Upload Plant Tissue Data File"
                    : "Upload Soil Data File"}
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 3 }}
                >
                  {dataType === 0
                    ? effectiveCropType === "cotton"
                      ? "Upload a CSV file containing plant tissue (biomass) data. The file will be parsed automatically, samples will be grouped by team using the plot mapping, and averages will be calculated for Total Carbon and Total Nitrogen. Data will be automatically assigned to the correct teams."
                      : "Upload a CSV file containing plant tissue data. The file will be parsed automatically, samples will be grouped by team using hardcoded mapping, and averages will be calculated for N, P, K values. Data will be automatically assigned to the correct teams."
                    : effectiveCropType === "cotton"
                      ? 'Upload a CSV file containing soil nitrate data for all teams at 2 depths (0-6", 6-12").'
                      : 'Upload a CSV file containing soil nitrate data for all teams at different depths (12", 24", 36").'}
                </Typography>

                {/* Date picker for Plant Tissue Data */}
                {dataType === 0 && (
                  <Box sx={{ mb: 3 }}>
                    <Typography
                      variant="subtitle2"
                      fontWeight="bold"
                      gutterBottom
                    >
                      Sample Date *
                    </Typography>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      display="block"
                      sx={{ mb: 1 }}
                    >
                      Select the date when the plant tissue samples were
                      collected. This date will be displayed to all teams.
                    </Typography>
                    <input
                      type="date"
                      value={plantTissueDate}
                      onChange={(e) => setPlantTissueDate(e.target.value)}
                      max={getTodayDate()}
                      required
                      style={{
                        padding: "10px 15px",
                        fontSize: "16px",
                        border: "2px solid #1976d2",
                        borderRadius: "8px",
                        outline: "none",
                        width: "250px",
                        fontFamily: "inherit",
                      }}
                    />
                  </Box>
                )}

                {/* Date picker for Soil Data */}
                {dataType === 1 && (
                  <Box sx={{ mb: 3 }}>
                    <Typography
                      variant="subtitle2"
                      fontWeight="bold"
                      gutterBottom
                    >
                      Sample Date *
                    </Typography>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      display="block"
                      sx={{ mb: 1 }}
                    >
                      Select the date when the soil samples were collected. This
                      date will be displayed to all teams.
                    </Typography>
                    <input
                      type="date"
                      value={soilDataDate}
                      onChange={(e) => setSoilDataDate(e.target.value)}
                      max={getTodayDate()}
                      required
                      style={{
                        padding: "10px 15px",
                        fontSize: "16px",
                        border: "2px solid #1976d2",
                        borderRadius: "8px",
                        outline: "none",
                        width: "250px",
                        fontFamily: "inherit",
                      }}
                    />
                  </Box>
                )}

                <Box
                  sx={{
                    display: "flex",
                    gap: 2,
                    alignItems: "center",
                    flexWrap: "wrap",
                    mb: 3,
                  }}
                >
                  <Button
                    variant="outlined"
                    component="label"
                    startIcon={<CloudUpload />}
                    size="large"
                  >
                    Choose File
                    <input
                      type="file"
                      accept=".csv,.xlsx,.xls"
                      hidden
                      onChange={handleFieldDataFileChange}
                    />
                  </Button>
                  {fieldDataFile && (
                    <Chip
                      label={fieldDataFile.name}
                      onDelete={() => setFieldDataFile(null)}
                      color="primary"
                      variant="outlined"
                      sx={{ maxWidth: "300px" }}
                    />
                  )}
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleFieldDataFileUpload}
                    disabled={
                      !fieldDataFile ||
                      uploadingFieldData ||
                      (dataType === 1 && !soilDataDate) ||
                      (dataType === 0 && !plantTissueDate)
                    }
                    startIcon={
                      uploadingFieldData ? (
                        <CircularProgress size={20} />
                      ) : (
                        <CloudUpload />
                      )
                    }
                    size="large"
                  >
                    {uploadingFieldData ? "Processing..." : "Upload & Parse"}
                  </Button>
                </Box>

                {/* Display parsed data preview */}
                {parsedFieldData && parsedFieldData.length > 0 && (
                  <Box sx={{ mt: 3 }}>
                    <Typography
                      variant="subtitle2"
                      fontWeight="bold"
                      gutterBottom
                      color="success.main"
                    >
                      ✓ Parsed Data Preview ({parsedFieldData.length} teams)
                    </Typography>
                    <TableContainer
                      component={Paper}
                      sx={{ maxHeight: 500, border: "1px solid #e0e0e0" }}
                    >
                      <Table stickyHeader>
                        <TableHead>
                          <TableRow>
                            <TableCell
                              sx={{
                                bgcolor: "#667eea",
                                color: "white",
                                fontWeight: "bold",
                              }}
                            >
                              Team
                            </TableCell>
                            <TableCell
                              sx={{
                                bgcolor: "#667eea",
                                color: "white",
                                fontWeight: "bold",
                              }}
                            >
                              Field ID
                            </TableCell>
                            {dataType === 0 && effectiveCropType === "cotton" ? (
                              <>
                                <TableCell
                                  align="center"
                                  sx={{
                                    bgcolor: "#667eea",
                                    color: "white",
                                    fontWeight: "bold",
                                  }}
                                >
                                  Avg Total Carbon (%)
                                </TableCell>
                                <TableCell
                                  align="center"
                                  sx={{
                                    bgcolor: "#667eea",
                                    color: "white",
                                    fontWeight: "bold",
                                  }}
                                >
                                  Avg Total Nitrogen (%)
                                </TableCell>
                                <TableCell
                                  align="center"
                                  sx={{
                                    bgcolor: "#667eea",
                                    color: "white",
                                    fontWeight: "bold",
                                  }}
                                >
                                  Plots
                                </TableCell>
                              </>
                            ) : dataType === 0 ? (
                              <>
                                <TableCell
                                  align="center"
                                  sx={{
                                    bgcolor: "#667eea",
                                    color: "white",
                                    fontWeight: "bold",
                                  }}
                                >
                                  Avg N (%)
                                </TableCell>
                                <TableCell
                                  align="center"
                                  sx={{
                                    bgcolor: "#667eea",
                                    color: "white",
                                    fontWeight: "bold",
                                  }}
                                >
                                  Avg P (%)
                                </TableCell>
                                <TableCell
                                  align="center"
                                  sx={{
                                    bgcolor: "#667eea",
                                    color: "white",
                                    fontWeight: "bold",
                                  }}
                                >
                                  Avg K (%)
                                </TableCell>
                                <TableCell
                                  align="center"
                                  sx={{
                                    bgcolor: "#667eea",
                                    color: "white",
                                    fontWeight: "bold",
                                  }}
                                >
                                  Plots
                                </TableCell>
                              </>
                            ) : effectiveCropType === "cotton" ? (
                              <>
                                <TableCell
                                  align="center"
                                  sx={{
                                    bgcolor: "#667eea",
                                    color: "white",
                                    fontWeight: "bold",
                                  }}
                                >
                                  NO3-N 0-6" (ppm)
                                </TableCell>
                                <TableCell
                                  align="center"
                                  sx={{
                                    bgcolor: "#667eea",
                                    color: "white",
                                    fontWeight: "bold",
                                  }}
                                >
                                  NO3-N 6-12" (ppm)
                                </TableCell>
                              </>
                            ) : (
                              <>
                                <TableCell
                                  align="center"
                                  sx={{
                                    bgcolor: "#667eea",
                                    color: "white",
                                    fontWeight: "bold",
                                  }}
                                >
                                  NO3-N 12" (ppm)
                                </TableCell>
                                <TableCell
                                  align="center"
                                  sx={{
                                    bgcolor: "#667eea",
                                    color: "white",
                                    fontWeight: "bold",
                                  }}
                                >
                                  NO3-N 24" (ppm)
                                </TableCell>
                                <TableCell
                                  align="center"
                                  sx={{
                                    bgcolor: "#667eea",
                                    color: "white",
                                    fontWeight: "bold",
                                  }}
                                >
                                  NO3-N 36" (ppm)
                                </TableCell>
                              </>
                            )}
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {parsedFieldData.map((team, index) => (
                            <TableRow
                              key={index}
                              sx={{
                                "&:hover": { bgcolor: "#f5f5f5" },
                                "&:nth-of-type(odd)": { bgcolor: "#fafafa" },
                              }}
                            >
                              <TableCell sx={{ fontWeight: "bold" }}>
                                {team.team}
                              </TableCell>
                              <TableCell>{team.fieldID}</TableCell>
                              {dataType === 0 && effectiveCropType === "cotton" ? (
                                <>
                                  <TableCell align="center">
                                    {team.avgTotalCarbon}
                                  </TableCell>
                                  <TableCell align="center">
                                    {team.avgTotalNitrogen}
                                  </TableCell>
                                  <TableCell align="center">
                                    {team.plotCount}
                                  </TableCell>
                                </>
                              ) : dataType === 0 ? (
                                <>
                                  <TableCell align="center">
                                    {team.avgN}
                                  </TableCell>
                                  <TableCell align="center">
                                    {team.avgP}
                                  </TableCell>
                                  <TableCell align="center">
                                    {team.avgK}
                                  </TableCell>
                                  <TableCell align="center">
                                    {team.plotCount}
                                  </TableCell>
                                </>
                              ) : effectiveCropType === "cotton" ? (
                                <>
                                  <TableCell align="center">
                                    {team.depth0_6}
                                  </TableCell>
                                  <TableCell align="center">
                                    {team.depth6_12}
                                  </TableCell>
                                </>
                              ) : (
                                <>
                                  <TableCell align="center">
                                    {team.depth12}
                                  </TableCell>
                                  <TableCell align="center">
                                    {team.depth24}
                                  </TableCell>
                                  <TableCell align="center">
                                    {team.depth36}
                                  </TableCell>
                                </>
                              )}
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>

                    {/* Clear Button */}
                    <Box
                      sx={{
                        mt: 3,
                        display: "flex",
                        justifyContent: "flex-end",
                        gap: 2,
                      }}
                    >
                      <Button
                        variant="outlined"
                        onClick={() => {
                          setParsedFieldData(null);
                        }}
                      >
                        Clear Preview
                      </Button>
                    </Box>
                  </Box>
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Instructions */}
        {/* <Grid item xs={12}>
          <Card elevation={3}>
            <CardContent>
              <Typography variant="h6" gutterBottom color="primary">
                Instructions
              </Typography>
              <Divider sx={{ my: 2 }} />
              <Typography variant="body2" component="div">
                <ul style={{ margin: 0, paddingLeft: '20px' }}>
                  <li>Select the data type (Plant Tissue or Soil)</li>
                  <li>Choose a CSV file containing data for all teams</li>
                  <li>Click "Upload & Parse" to process the file</li>
                  <li>Review the parsed data preview to verify accuracy</li>
                  <li>Data will be automatically saved to the database</li>
                </ul>
              </Typography>
            </CardContent>
          </Card>
        </Grid> */}
      </Grid>
    </Container>
  );
}

export default FieldDataManagement;
